import { useState } from 'react';
import { Button, ConfirmationDialog } from '@roger/ui-components';
import { unsetEpisodeBilledExternally } from '../../services/api/sdk.gen';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';

type Props = {
  episodeId: string;
  onSuccess: () => void;
  onError: (title: string, description: string) => void;
};

/**
 * Roger super-user migration tool: clears ``billed_externally`` only. Does not
 * run Take Over, but the next billing run bills the episode from its start date.
 */
export function UnsetBilledExternallyButton({
  episodeId,
  onSuccess,
  onError,
}: Props) {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const { error } = await unsetEpisodeBilledExternally({
        path: { episode_id: episodeId },
      });
      if (error) throw error;
      setOpen(false);
      onSuccess();
    } catch (error) {
      console.error('Failed to unset billed externally:', error);
      onError(
        'Unable to clear flag',
        getBusinessErrorMessage(error) ??
          'Something went wrong. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button
        type='button'
        variant='outline'
        size='sm'
        onClick={() => setOpen(true)}
        data-testid='unset-billed-externally'
      >
        Clear billed externally
      </Button>
      <ConfirmationDialog
        open={open}
        title='Clear "billed externally"?'
        message='Internal migration tool. Turns off billed externally for this episode only and does not run Take Over. Roger will bill this episode from its start date on the next billing run, so only continue if the prior EMR did not bill any part of it.'
        confirmLabel='Clear flag'
        loading={isSubmitting}
        onAccept={() => void handleConfirm()}
        onCancel={() => setOpen(false)}
      />
    </>
  );
}
